import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Download,
  Loader2,
  MapPin,
  RefreshCw,
} from "lucide-react";

import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import { OnboardingBrandMark } from "@/components/onboarding/OnboardingBrandMark";
import { OnboardingStepTag } from "@/components/onboarding/OnboardingStepTag";
import { OnboardingBrandColumn } from "@/components/onboarding/OnboardingBrandColumn";
import { getCertificateTrail } from "@/components/onboarding/OnboardingTrail";
import { NotarialCertificateSection } from "@/pages/onboarding/NotarialCertificateSection";
import type { SituationChoice } from "@/pages/onboarding/OnboardingSituationStep";

export interface OnboardingAbitabPresentStepProps {
  /** When null, the Abitab automator hasn't returned the request number
   *  yet — render the editorial "Procesando solicitud" pending state
   *  instead of the request-number card. */
  idDigitalRequestNumber: string | null;
  /** Backend cert-request status. When "failed" we surface a retry UI
   *  instead of leaving the user staring at an endless spinner. */
  certificateRequestStatus?:
    | "pending"
    | "in_progress"
    | "fulfilled"
    | "failed"
    | null;
  /** Optional retry handler for the failed state. */
  onRetryAutomation?: () => void;
  retrying?: boolean;
  comprobantePdfUrl: string | null;
  companyType: "unipersonal" | "sas" | "srl" | "sa" | "monotributo" | null;
  situationChoice: SituationChoice | null;
  hasCfeImportNext: boolean;
  /** When true, gates the Siguiente CTA on the notarial acknowledgment
   *  checkbox (SAS/SRL/SA companies). */
  onNotarialCanAdvanceChange: (v: boolean) => void;
  notarialCanAdvance: boolean;
  onBack?: () => void;
  onNext: () => void;
  nextDisabled?: boolean;
  nextLoading?: boolean;
}

/**
 * Editorial "Tu solicitud está lista — andá a Abitab" step. Replaces the
 * legacy FiscaloCertificateProgressCard + raw NotarialCertificateSection
 * combo that lived inside `<div className="space-y-4">` chrome. Renders
 * inside OnboardingShell with its own brand column and inline actions.
 */
export function OnboardingAbitabPresentStep({
  idDigitalRequestNumber,
  certificateRequestStatus = null,
  onRetryAutomation,
  retrying = false,
  comprobantePdfUrl,
  companyType,
  situationChoice,
  hasCfeImportNext,
  onNotarialCanAdvanceChange,
  notarialCanAdvance,
  onBack,
  onNext,
  nextDisabled = false,
  nextLoading = false,
}: OnboardingAbitabPresentStepProps): JSX.Element {
  const { t } = useTranslation();
  const isCertBeforeDgi =
    situationChoice === "scratch" ||
    situationChoice === "issuer_need_regularization";
  const requiresNotarial = ["sas", "sa", "srl"].includes(companyType ?? "");
  // Certificado row: scratch (DGI-first) is row 4; cert-first regularization
  // and already_issuer are row 3.

  // The CERTIFICATE trail, not the journey trail. This screen sits inside the
  // certificate process and says so in its eyebrow; a pane listing Situación
  // and Acceso DGI was describing a different, already-finished process.
  const trailSteps = getCertificateTrail("fiscalo");
  const stepTagTotal = trailSteps.length;
  const isFailed =
    certificateRequestStatus === "failed" && idDigitalRequestNumber === null;
  const isPending = !isFailed && idDigitalRequestNumber === null;
  // Row within the CERTIFICATE trail: Solicitud (2) while ID Digital is still
  // producing the request number or the automation failed producing it, Abitab
  // (3) once there is a number to carry to the counter.
  const stepTagStep = isPending || isFailed ? 2 : 3;

  return (
    <OnboardingShell
      form={
        <>
          <OnboardingBrandMark />
          <OnboardingStepTag
            step={stepTagStep}
            total={stepTagTotal}
            label={
              isFailed
                ? t(
                    "onboarding.abitabPresent.tagFailed",
                    "Solicitud incompleta",
                  )
                : isPending
                  ? t(
                      "onboarding.abitabPresent.tagPending",
                      "Procesando solicitud",
                    )
                  : t(
                      "onboarding.abitabPresent.tag",
                      "Pasá por Abitab",
                    )
            }
            flow={t(
              "onboarding.abitabPresent.flow",
              "Certificado digital",
            )}
          />
          <h1 className="fx-onb-h1">
            {isFailed ? (
              <>
                {t(
                  "onboarding.abitabPresent.h1LeadFailed",
                  "No pudimos completar",
                )}{" "}
                <span className="ital">
                  {t(
                    "onboarding.abitabPresent.h1AccentFailed",
                    "tu solicitud en ID Digital.",
                  )}
                </span>
              </>
            ) : isPending ? (
              <>
                {t(
                  "onboarding.abitabPresent.h1LeadPending",
                  "Estamos preparando",
                )}{" "}
                <span className="ital">
                  {t(
                    "onboarding.abitabPresent.h1AccentPending",
                    "tu solicitud de certificado.",
                  )}
                </span>
              </>
            ) : (
              <>
                {t(
                  "onboarding.abitabPresent.h1Lead",
                  "Tu solicitud está lista,",
                )}{" "}
                <span className="ital">
                  {t(
                    "onboarding.abitabPresent.h1Accent",
                    "ahora pasá por Abitab.",
                  )}
                </span>
              </>
            )}
          </h1>
          <p className="fx-onb-lead">
            {isFailed
              ? t(
                  "onboarding.abitabPresent.leadFailed",
                  "La automatización con ID Digital no pudo terminar el formulario. Podés reintentarla — si vuelve a fallar, escribinos y un agente lo completa por vos.",
                )
              : isPending
                ? t(
                    "onboarding.abitabPresent.leadPending",
                    "ID Digital está generando tu número de solicitud. Esto puede tardar unos minutos. Cuando esté listo lo vas a ver acá mismo y te llega también por mail con el comprobante para Abitab.",
                  )
                : t(
                    "onboarding.abitabPresent.lead",
                    "Llevá este número de solicitud (y el comprobante adjunto en tu email) a un local de Abitab para completar el pago. Cuando lo paguen, ID Digital genera tu certificado y nosotros lo subimos a Fiscalo automáticamente.",
                  )}
          </p>

          {isFailed ? (
            /* Failed card — the cert request exists but the Abitab
               automator died mid-flow. Surface the failure and offer a
               retry path so the user isn't staring at a forever-spinner. */
            <div
              style={{
                marginTop: 16,
                border: "1px solid color-mix(in srgb, #C58F2E 35%, var(--fx-ink-200))",
                borderRadius: "var(--fx-r-md, 12px)",
                background: "color-mix(in srgb, #C58F2E 6%, var(--fx-paper-2))",
                padding: "22px 22px 24px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontFamily: "var(--fx-ff-mono)",
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: "0.14em",
                  color: "#8A6418",
                  marginBottom: 14,
                }}
              >
                <AlertTriangle aria-hidden style={{ width: 14, height: 14 }} />
                {t(
                  "onboarding.abitabPresent.failedLabel",
                  "Algo falló",
                )}
              </div>
              <p
                style={{
                  margin: 0,
                  fontFamily: "var(--fx-ff-serif)",
                  fontSize: 18,
                  fontWeight: 500,
                  color: "var(--fx-s-text)",
                  lineHeight: 1.4,
                }}
              >
                {t(
                  "onboarding.abitabPresent.failedTitle",
                  "La automatización con ID Digital se interrumpió.",
                )}
              </p>
              <p
                style={{
                  margin: "10px 0 16px",
                  fontSize: 13.5,
                  lineHeight: 1.55,
                  color: "var(--fx-ink-500, #6B7280)",
                }}
              >
                {t(
                  "onboarding.abitabPresent.failedBody",
                  "Tu solicitud no se llegó a registrar en ID Digital. Reintentá la automatización; si vuelve a fallar, escribinos a certificados@fiscalo.uy y un agente lo termina por vos.",
                )}
              </p>
              {onRetryAutomation ? (
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-accent"
                  onClick={onRetryAutomation}
                  disabled={retrying}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  {retrying ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <RefreshCw aria-hidden style={{ width: 14, height: 14 }} />
                  )}
                  {t(
                    "onboarding.abitabPresent.failedRetry",
                    "Reintentar automatización",
                  )}
                </button>
              ) : null}
            </div>
          ) : isPending ? (
            /* Pending card — Abitab automator hasn't returned the
               request number yet. The query polls every 8s; when the
               number arrives, this component re-renders into the
               request-number variant automatically. */
            <div
              style={{
                marginTop: 16,
                border: "1px solid var(--fx-ink-200)",
                borderRadius: "var(--fx-r-md, 12px)",
                background: "var(--fx-s-raised-hover)",
                padding: "22px 22px 24px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontFamily: "var(--fx-ff-mono)",
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: "0.14em",
                  color: "var(--fx-ink-500, #6B7280)",
                  marginBottom: 14,
                }}
              >
                <Loader2
                  className="animate-spin"
                  aria-hidden
                  style={{ width: 14, height: 14 }}
                />
                {t(
                  "onboarding.abitabPresent.pendingLabel",
                  "En proceso",
                )}
              </div>
              <p
                style={{
                  margin: 0,
                  fontFamily: "var(--fx-ff-serif)",
                  fontSize: 18,
                  fontWeight: 500,
                  color: "var(--fx-s-text)",
                  lineHeight: 1.4,
                }}
              >
                {t(
                  "onboarding.abitabPresent.pendingTitle",
                  "Esperando que ID Digital procese tu solicitud.",
                )}
              </p>
              <p
                style={{
                  margin: "10px 0 0",
                  fontSize: 13.5,
                  lineHeight: 1.55,
                  color: "var(--fx-ink-500, #6B7280)",
                }}
              >
                {t(
                  "onboarding.abitabPresent.pendingBody",
                  "Apenas devuelvan el número y el comprobante, aparecen acá. Podés dejar esta pestaña abierta — se actualiza solo.",
                )}
              </p>
            </div>
          ) : (
            /* Request number card */
            <div
              style={{
                marginTop: 16,
                border: "1px solid var(--fx-ink-200)",
                borderRadius: "var(--fx-r-md, 12px)",
                background: "var(--fx-s-raised-hover)",
                padding: "20px 22px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontFamily: "var(--fx-ff-mono)",
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: "0.14em",
                  color: "var(--fx-ink-500, #6B7280)",
                  marginBottom: 10,
                }}
              >
                <MapPin aria-hidden style={{ width: 14, height: 14 }} />
                {t(
                  "onboarding.abitabPresent.requestNumberLabel",
                  "Número de solicitud (presentalo en Abitab)",
                )}
              </div>
              <p
                style={{
                  margin: 0,
                  fontFamily: "var(--fx-ff-mono)",
                  fontSize: 32,
                  fontWeight: 500,
                  letterSpacing: "0.04em",
                  color: "var(--fx-s-text)",
                  lineHeight: 1.1,
                }}
              >
                {idDigitalRequestNumber}
              </p>

              {comprobantePdfUrl ? (
                <div style={{ marginTop: 14 }}>
                  <a
                    href={comprobantePdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="fx-btn fx-btn-ghost"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      textDecoration: "none",
                    }}
                  >
                    <Download
                      aria-hidden
                      style={{ width: 14, height: 14 }}
                    />
                    {t(
                      "onboarding.abitabPresent.downloadComprobante",
                      "Descargar comprobante para Abitab",
                    )}
                  </a>
                </div>
              ) : null}
            </div>
          )}

          {!isPending && !isFailed && requiresNotarial ? (
            <div style={{ marginTop: 16 }}>
              <NotarialCertificateSection
                idPrefix="abitab-present"
                onCanAdvanceChange={onNotarialCanAdvanceChange}
              />
            </div>
          ) : null}

          <div className="fx-onb-actions">
            <div className="side">
              {onBack ? (
                <button
                  type="button"
                  className="fx-onb-btn fx-onb-btn-secondary"
                  onClick={onBack}
                  disabled={nextLoading || retrying}
                >
                  <ArrowLeft aria-hidden />
                  {t("onboarding.back", "Volver")}
                </button>
              ) : null}
            </div>
            <div className="side end">
              <button
                type="button"
                className="fx-onb-btn fx-onb-btn-accent"
                disabled={
                  isPending ||
                  isFailed ||
                  nextDisabled ||
                  nextLoading ||
                  (requiresNotarial && !notarialCanAdvance)
                }
                onClick={() => {
                  if (
                    isPending ||
                    isFailed ||
                    nextDisabled ||
                    nextLoading ||
                    (requiresNotarial && !notarialCanAdvance)
                  )
                    return;
                  onNext();
                }}
              >
                {nextLoading || isPending ? (
                  <>
                    <Loader2 className="animate-spin" />
                    {isPending
                      ? t(
                          "onboarding.abitabPresent.pendingCta",
                          "Esperando solicitud...",
                        )
                      : null}
                  </>
                ) : (
                  <>
                    {t(
                      "onboarding.abitabPresent.next",
                      "Ya fui a Abitab",
                    )}
                    <ArrowRight aria-hidden />
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      }
      brand={
        <OnboardingBrandColumn
          eyebrow={
            isPending
              ? t(
                  "onboarding.abitabPresent.brandEyebrowPending",
                  "Procesando",
                )
              : t(
                  "onboarding.abitabPresent.brandEyebrow",
                  "Casi listos",
                )
          }
          quote={
            isPending ? (
              <>
                {t(
                  "onboarding.abitabPresent.brandQuoteLeadPending",
                  "Estamos tramitando con ID Digital.",
                )}{" "}
                <span className="accent">
                  {t(
                    "onboarding.abitabPresent.brandQuoteAccentPending",
                    "Vos no tenés que hacer nada.",
                  )}
                </span>
              </>
            ) : (
              <>
                {t(
                  "onboarding.abitabPresent.brandQuoteLead",
                  "Un paso físico y termina el trámite.",
                )}{" "}
                <span className="accent">
                  {t(
                    "onboarding.abitabPresent.brandQuoteAccent",
                    "El resto lo automatizamos.",
                  )}
                </span>
              </>
            )
          }
          currentStep={stepTagStep}
          trailSteps={trailSteps}
          valueBlock={
            isPending
              ? {
                  head: t(
                    "onboarding.abitabPresent.brandValueHeadPending",
                    "Qué sigue",
                  ),
                  items: [
                    t(
                      "onboarding.abitabPresent.brandValuePending1",
                      "ID Digital nos devuelve el número de solicitud",
                    ),
                    t(
                      "onboarding.abitabPresent.brandValuePending2",
                      "Te mostramos el número acá + te lo mandamos por mail",
                    ),
                    t(
                      "onboarding.abitabPresent.brandValuePending3",
                      "Vas a un local de Abitab a pagar el certificado",
                    ),
                    t(
                      "onboarding.abitabPresent.brandValuePending4",
                      "Lo subimos a Fiscalo automáticamente",
                    ),
                  ],
                }
              : {
                  head: t(
                    "onboarding.abitabPresent.brandValueHead",
                    "Qué llevar a Abitab",
                  ),
                  items: [
                    t(
                      "onboarding.abitabPresent.brandValueItem1",
                      "Tu número de solicitud (de arriba)",
                    ),
                    t(
                      "onboarding.abitabPresent.brandValueItem2",
                      "El comprobante (en tu email o descargalo acá)",
                    ),
                    t(
                      "onboarding.abitabPresent.brandValueItem3",
                      "Cédula de identidad",
                    ),
                    ...(requiresNotarial
                      ? [
                          t(
                            "onboarding.abitabPresent.brandValueItem4Notarial",
                            "Certificado notarial (porque sos SAS/SRL/SA)",
                          ),
                        ]
                      : []),
                  ],
                }
          }
        />
      }
    />
  );
}
